import PinView from "./view";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendPin, setUser, validatePin } from "@/redux/ducks/user";
import { ReducersTypes } from "@/redux/reducers/Models";
import useRedirect from "@/hooks/useRedirect";
import { useNavigate } from "react-router-dom";

export default function Pin() {
  useRedirect("ticket/pin");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(
    (
      state: ReducersTypes & {
        userReducer: {
          pin: string;
        };
      },
    ) => state.userReducer,
  );
  const TIME_AGAIN = 1000;

  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [pinAgain, setPinAgain] = useState(false);
  const [error, setError] = useState(false);

  const goBack = () => navigate("/ticket/user");

  const goToTicket = () => {
    navigate("/ticket");
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    dispatch(setUser({ pin: value }));
  };

  const getIsDisabled = () => {
    if (user?.pin?.length < 5) return true;
    return false;
  };

  const handleSendPin = () => {
    if (isLoading) return;

    setIsLoading(true);

    dispatch(
      sendPin({
        document: user?.document as string,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        callback: (_) => {
          setIsLoading(false);
        },
      }),
    );
  };

  const handleValidatePin = (success: boolean, errorMessage?: string) => {
    setIsLoading(false);

    if (!success) {
      if (errorMessage) setError(true);
      return;
    }

    goToTicket();
  };

  const handleSubmit = () => {
    if (isLoading || getIsDisabled()) return;

    setIsLoading(true);

    dispatch(
      validatePin({
        document: user?.document as string,
        pin: user?.pin,
        callback: handleValidatePin,
      }),
    );
  };

  const handleTryAgain = () => {
    if (timer !== 0 || isLoading) return;
    setIsLoading(true);
    setTimer(30);
    handleSendPin();
  };

  const updateCount = useCallback(() => {
    if (timer > 0) {
      setTimeout(() => {
        setTimer(timer - 1);
      }, TIME_AGAIN);
    }
  }, [timer]);

  useEffect(() => {
    if (timer !== 0) {
      updateCount();
      if (!pinAgain) setPinAgain(true);
    } else if (pinAgain) setPinAgain(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer, setPinAgain, updateCount]);

  useEffect(() => {
    if (error) setError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.pin]);

  useEffect(() => {
    dispatch(setUser({ pin: "" }));
    handleSendPin();
    updateCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PinView
      handleTryAgain={handleTryAgain}
      timer={timer}
      isLoading={isLoading}
      handleSubmit={handleSubmit}
      pin={user?.pin}
      onChange={onChange}
      error={error}
      goBack={goBack}
      isDisabled={getIsDisabled()}
    />
  );
}
